// Props de demonstração da composição Reels para a aba Estilo: o preview é a MESMA
// composição do render, com o estilo escolhido aplicado por cima. Quando a Fase 2
// ainda não rodou, as legendas saem das falas dos takes (quote), distribuídas sobre
// o tempo de imagem de cada take no cut.mp4 — o preview já fala o texto do vídeo.
import type {
  CaptionCue, CaptionWord, EdvidCutState, EdvidVisualState, EditData, ReelsProps, StyleSetup,
} from '../../../types/videoEdit';

export type VisualProps = EdvidVisualState['props'];

export function segmentsFromCut(cut: EdvidCutState): Array<{ start: number; dur: number }> {
  const jt = cut.edl.jcut_timeline;
  if (jt?.length) return jt.map(j => ({ start: j.video_start_in_output, dur: j.video_duration }));
  let t = 0;
  return cut.edl.ranges.map(r => { const s = { start: t, dur: r.end - r.start }; t += s.dur; return s; });
}

/** Palavras da fala de cada take espalhadas pela duração da imagem dele (peso por nº de letras). */
export function captionsFromQuotes(cut: EdvidCutState): CaptionWord[] {
  const segs = segmentsFromCut(cut);
  const words: CaptionWord[] = [];
  cut.edl.ranges.forEach((r, i) => {
    const seg = segs[i];
    if (!seg || !r.quote) return;
    const toks = r.quote.split(/\s+/).filter(Boolean);
    const weights = toks.map(w => Math.max(2, w.length));
    const sum = weights.reduce((a, b) => a + b, 0);
    let t = seg.start + 0.05;
    const span = Math.max(0.2, seg.dur - 0.1);
    toks.forEach((w, k) => {
      const d = (weights[k] / sum) * span;
      const startMs = Math.round(t * 1000);
      const endMs = Math.round((t + d) * 1000);
      words.push({ text: w, startMs, endMs, timestampMs: startMs, confidence: null });
      t += d;
    });
  });
  return words;
}

/** caption-cues mínimos para o estilo empilhado quando o backend ainda não gerou os dele. */
export function demoCues(words: CaptionWord[]): CaptionCue[] {
  const cues: CaptionCue[] = [];
  let i = 0;
  let n = 0;
  while (i < words.length) {
    const lines: CaptionCue['lines'] = [];
    for (let l = 0; l < 3 && i < words.length; l++) {
      const take = l === 1 ? 3 : 2;
      const chunk = words.slice(i, i + take);
      i += chunk.length;
      lines.push(chunk.map(w => ({ text: w.text, fromMs: w.startMs, toMs: w.endMs })));
      if (/[.!?]$/.test(chunk[chunk.length - 1]?.text || '')) break;
    }
    const flat = lines.flat();
    cues.push({
      i: n++,
      startMs: flat[0].fromMs,
      endMs: flat[flat.length - 1].toMs,
      preset: 'STACK_MIXED',
      exit: 'blur_up',
      styleOffset: 0,
      lineStyles: lines.map((_, k) => k % 3),
      lineBoost: lines.map(() => false),
      lineEmph: lines.map((_, k) => k === 2),
      lines,
    });
  }
  return cues;
}

export function twoLines(text: string): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [text.trim()];
  let best = 1; let bestD = Infinity;
  for (let i = 1; i < words.length; i++) {
    const d = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
    if (d < bestD) { bestD = d; best = i; }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
}

const ZOOM_CYCLE = [1.1, 1.16, 1.22, 1.13, 1.19];

export function baseDemoProps(cut: EdvidCutState): VisualProps {
  const segments = segmentsFromCut(cut);
  const captions = captionsFromQuotes(cut);
  const firstQuote = cut.edl.ranges[0]?.quote || 'O seu gancho aparece aqui';
  const hookText = firstQuote.split(/\s+/).slice(0, 8).join(' ');
  const editData: EditData = {
    width: 1080,
    height: 1920,
    fps: cut.fps || 30,
    durationSec: cut.durationSec,
    camera: { enabled: true, zooms: segments.map((_, i) => ZOOM_CYCLE[i % ZOOM_CYCLE.length]), pushIn: 0.04, targetX: 0.5, targetY: 0.42 },
    hook: { enabled: true, endSec: Math.min(4, segments[0]?.dur || 4), text: hookText, lines: twoLines(hookText), style: 'outline', logo: null, sign: null },
    captions: { enabled: true, style: 'karaoke', fontSize: 61, maxWords: 3, safeWidth: 900, paddingBottom: 520 },
    inserts: [],
    behind: [],
    splitInserts: [],
    transitions: [],
    soundtrack: { enabled: false, file: '', volume: 0.1 },
  };
  return { editData, captions, captionCues: demoCues(captions), segments: { segments }, track: null };
}

/** Aplica o estilo do portão sobre props (demo ou as da Fase 2) — só o que o estilo decide. */
export function applyStyle(base: VisualProps, style: StyleSetup): VisualProps {
  const ed = base.editData;
  const segs = base.segments.segments;
  const hookText = style.hookText?.trim();
  return {
    ...base,
    captionCues: base.captionCues.length ? base.captionCues : demoCues(base.captions),
    editData: {
      ...ed,
      camera: {
        ...ed.camera,
        enabled: style.elements.zoomAuto || style.elements.zoomCuts || style.elements.tracking,
        pushIn: style.elements.zoomAuto ? (ed.camera.pushIn || 0.04) : 0,
        zooms: style.elements.zoomCuts ? segs.map((_, i) => ed.camera.zooms[i] || ZOOM_CYCLE[i % ZOOM_CYCLE.length]) : segs.map(() => 1),
        tracking: style.elements.tracking,
      },
      hook: {
        ...ed.hook,
        enabled: style.headline !== 'none',
        ...(style.headline !== 'none' ? { style: style.headline } : {}),
        accent: style.accent,
        ...(hookText ? { text: hookText, lines: twoLines(hookText) } : {}),
        // formatos com mais caixas: o que o usuário digitou aparece na hora na prévia
        ...(style.headline === 'empilhado' && (style.hookParts || []).some(p => p?.trim())
          ? { parts: [0, 1, 2].map(i => style.hookParts?.[i]?.trim() || '') }
          : {}),
        ...(style.headline === 'pergunta'
          ? {
              question: {
                title: style.hookQuestion?.title?.trim() || 'O que você me perguntaria?',
                text: style.hookQuestion?.text?.trim() || hookText || ed.hook.question?.text || ed.hook.text || 'Qual a sua pergunta?',
              },
              endSec: ed.durationSec,
            }
          : {}),
      },
      captions: {
        ...ed.captions,
        enabled: style.captions !== 'none',
        ...(style.captions !== 'none' ? { style: style.captions } : {}),
        accent: style.accent,
      },
      transitions: style.elements.flashCut ? (ed.transitions || []) : [],
      // a trilha do elemento "música" é do Instagram, anexada na publicação — não entra na composição
    },
  };
}

export function withVideo(props: VisualProps, videoSrc: string): ReelsProps {
  return { ...props, videoSrc };
}
